import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { SelectOption } from '@/components';
import type { BookingRefundDestination } from '@/types';

import { Select } from '@/components';
import { InputLabel } from '@/components/InputLabel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { cashRegisterQueries } from '@/queries';

import { KIND_ICONS } from '@/constants';

const DEPOSIT_TARGET = 'deposit';
const REGISTER_TARGET = 'cash_register';

type CancelBookingDialogProps = {
  isOpen: boolean;
  cashRegistersUrl: string;
  refundReasons: SelectOption[];
  isPending: boolean;
  /** Defaults to the wording for one booking; an event or a class names what else it releases. */
  title?: string;
  description?: string;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: (data: BookingRefundDestination) => void;
};

/**
 * Cancelling a booking, a class or a whole event asks the same question: where a purchase made for
 * it goes back — the client's deposit or an outflow from one register. Even a LiqPay refund is manual.
 */
export const CancelBookingDialog = (props: CancelBookingDialogProps) => {
  const {
    isOpen,
    cashRegistersUrl,
    refundReasons,
    isPending,
    title,
    description,
    onOpenChange,
    onConfirm,
  } = props;
  const { t } = useTranslation();
  const targetId = useId();

  const [refundTarget, setRefundTarget] = useState<string>(DEPOSIT_TARGET);
  const [cashRegisterId, setCashRegisterId] = useState('');
  const [refundReasonId, setRefundReasonId] = useState('');

  const isRegisterPicked = refundTarget === REGISTER_TARGET;

  const { data: cashRegistersData } = useQuery(
    cashRegisterQueries.accessibleOptions({
      url: cashRegistersUrl,
      isEnabled: isOpen && isRegisterPicked,
    }),
  );

  const cashRegisterOptions: SelectOption[] = useMemo(
    () =>
      (cashRegistersData?.data ?? []).map((register) => {
        const Icon = KIND_ICONS[register.kind];

        return {
          id: String(register.id),
          name: `${register.name} (${register.location_name})`,
          icon: Icon ? <Icon className="size-4" /> : undefined,
        };
      }),
    [cashRegistersData],
  );

  const isRegisterMissing = isRegisterPicked && cashRegisterId === '';

  const onConfirmClick = useCallback(() => {
    onConfirm({
      refund_target: isRegisterPicked ? REGISTER_TARGET : DEPOSIT_TARGET,
      cash_register_id: isRegisterPicked ? Number(cashRegisterId) : null,
      refund_reason_id: refundReasonId ? Number(refundReasonId) : null,
    });
  }, [cashRegisterId, isRegisterPicked, onConfirm, refundReasonId]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title ?? t('Cancel this booking?')}</DialogTitle>
          <DialogDescription>
            {description ??
              t(
                'The trainer’s time is freed. A purchase made for this booking is refunded.',
              )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <InputLabel label={t('Refund method')} />
          <RadioGroup
            value={refundTarget}
            onValueChange={setRefundTarget}
            className="grid gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={DEPOSIT_TARGET}
                id={`${targetId}-deposit`}
              />
              <Label
                htmlFor={`${targetId}-deposit`}
                className="font-normal"
              >
                {t("Credit to the client's deposit")}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={REGISTER_TARGET}
                id={`${targetId}-register`}
              />
              <Label
                htmlFor={`${targetId}-register`}
                className="font-normal"
              >
                {t('Pay out from cash register')}
              </Label>
            </div>
          </RadioGroup>

          {isRegisterPicked && (
            <Select
              label={t('Cash register')}
              isRequired
              value={cashRegisterId}
              onValueChange={setCashRegisterId}
              options={cashRegisterOptions}
            />
          )}

          <Select
            label={t('Refund reason')}
            value={refundReasonId}
            onValueChange={setRefundReasonId}
            options={refundReasons}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('Close')}
          </Button>
          <Button
            onClick={onConfirmClick}
            disabled={isPending || isRegisterMissing}
          >
            {t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
